import { DEFAULT_LOCALE } from '../constants/price';

export const formatDate = (
  isoDate: string | number | Date,
  options?: Intl.DateTimeFormatOptions,
  locale: string = DEFAULT_LOCALE,
): string => {
  return Intl.DateTimeFormat(locale, options).format(new Date(isoDate));
};

export const naturalLanguageFormat = (
  isoDate: string,
  locale: string = DEFAULT_LOCALE,
): string =>
  new Date(isoDate).toLocaleDateString(locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

export const formatDateToPart = (
  isoDate: string | number | Date,
): { day: string; month: string; weekday: string; year: string } => {
  const parts = new Intl.DateTimeFormat('fr', {
    day: '2-digit',
    month: 'short',
    weekday: 'short',
    year: 'numeric',
  }).formatToParts(new Date(isoDate));

  return parts.reduce(
    (acc, { type, value }) => {
      acc[type] = value;

      return acc;
    },
    { day: '', month: '', weekday: '', year: '' },
  );
};

type IContainsDate<Key extends string> = {
  [K in Key]: string;
};

export const isoDateSort =
  <T extends string>(key: T) =>
  (a: IContainsDate<T>, b: IContainsDate<T>) => {
    return a[key] < b[key] ? -1 : a[key] > b[key] ? 1 : 0;
  };

export const formatDateToAbbreviation = (
  date: string | Date,
  locale: Intl.LocalesArgument = DEFAULT_LOCALE,
): string => {
  return new Date(date)
    .toLocaleDateString(locale, {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    })
    .replace(',', '.');
};

export const formatDateToLocale = (
  timestamp: number | Date,
  locale: string = DEFAULT_LOCALE,
) => {
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(timestamp);
};

export const isValidDate = (dateString: string) => {
  const dateParts = dateString.split(/[/.]/);

  if (dateParts.length !== 3) {
    return false;
  }
  const day = parseInt(dateParts[0], 10);
  const month = parseInt(dateParts[1], 10);
  const year = parseInt(dateParts[2], 10);

  if (
    isNaN(day) ||
    isNaN(month) ||
    isNaN(year) ||
    day < 1 ||
    day > 31 ||
    month < 1 ||
    month > 12 ||
    year.toString().length !== 4
  ) {
    return false;
  }

  // Check for days in February and leap years
  if (month === 2) {
    const isLeap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;

    if (day > (isLeap ? 29 : 28)) {
      return false;
    }
  }

  // Check for months with 30 days
  if ([4, 6, 9, 11].includes(month) && day > 30) {
    return false;
  }

  return true;
};

export const formatDateToShort = (dateString: string): string => {
  const date = new Date(dateString);

  const formatter = new Intl.DateTimeFormat('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

  return formatter.format(date).replace(/\.$/, '');
};
